/**
 * Pure helpers for Firm settings → Vendor knowledge base (T-0206, T-0207):
 * labels, plain words for the server's codes, dates, and the quote tokens a
 * curator narrows a quote with. Nothing here counts knowledge: every count
 * on these screens is the server's.
 */

import { CLIENT_NAME_REMOVED } from '@/components/ui/citation-chip';

export const PLATFORM_TYPES = [
  { id: 'erp', label: 'ERP' },
  { id: 'hcm', label: 'HCM' },
  { id: 'cis', label: 'Utility billing (CIS)' },
  { id: 'eam', label: 'Asset management (EAM)' },
  { id: 'elm', label: 'Land management (ELM)' },
  { id: 'document_management', label: 'Document management (EDMS)' },
  { id: 'public_safety', label: 'Public safety' },
  { id: 'other', label: 'Other' },
] as const;

export function platformLabel(id: string | null | undefined): string {
  return PLATFORM_TYPES.find((t) => t.id === id)?.label ?? 'Other';
}

export const KIND_LABEL: Record<string, string> = {
  capability: 'Capability',
  limitation: 'Limitation',
  differentiator: 'Differentiator',
  requirement_response: 'Requirement answer',
  integration: 'Integration',
  hosting_security: 'Hosting and security',
  company: 'Company',
  overview: 'Overview',
};

export const ANSWER_KIND = 'requirement_response';

/** The kinds listed under Knowledge: everything but requirement answers. */
export const KNOWLEDGE_KINDS = Object.keys(KIND_LABEL)
  .filter((k) => k !== ANSWER_KIND)
  .join(',');

export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/** Why a curator declines a proposal (E13-F06 §2), in the prototype's words. */
export const DECLINE_REASONS = [
  { code: 'not_about_product', label: 'Not about this product' },
  { code: 'marketing_language', label: 'Marketing claim with no substance' },
  { code: 'already_known', label: 'Duplicate' },
  { code: 'wrong_passage', label: 'Wrong passage' },
  { code: 'other', label: 'Other' },
] as const;

/** Why the server skipped a proposal at confirm (E13-F06 §2), in plain words. */
export function skipReasonText(reason: string): string {
  switch (reason) {
    case 'already_decided':
      return 'already decided by someone else';
    case 'statement_required':
      return 'it needs a statement';
    case 'redaction_unreviewed':
      return 'a removed client name needs a look first';
    case 'needs_review':
      return 'it needs a look first';
    case 'v1_number':
      return 'its quote still has a rating from Caliber v1';
    case 'excerpt_not_found':
      return 'its quote is no longer in the capture';
    case 'statement_numbers':
      return 'its statement uses a number the quote does not have';
    default:
      return reason.replace(/_/g, ' ');
  }
}

/** A check's outcome, for a source row or the home page's recent list. */
export function outcomeText(outcome: string | null | undefined): string {
  switch (outcome) {
    case 'unchanged':
      return 'unchanged';
    case 'changed':
      return 'changed';
    case 'first_capture':
      return 'captured';
    case 'not_found':
    case 'gone':
      return 'gone';
    case 'moved':
      return 'moved';
    case 'blocked_robots':
      return 'blocked by robots.txt';
    case 'refused_address':
      return 'refused: not a public address';
    case 'refused_domain':
      return 'refused: not on an approved domain';
    case 'too_large':
      return 'too large';
    case 'wrong_type':
      return 'not a web page';
    case 'unreachable':
      return 'could not be reached';
    case 'needs_browser':
      return 'needs a browser to show its text';
    case 'over_cap':
      return 'waiting: over the reading limit';
    case 'read':
      return 'read';
    default:
      return outcome ? outcome.replace(/_/g, ' ') : 'done';
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toDate(value: string): Date | null {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00Z`) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "8 Oct 2026" (UTC, as the server writes citation dates). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` : '';
}

/** "8 Oct". */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : '';
}

export function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

/** "1 item", "3 items", "2 implementation partners". */
export function plural(n: number, word: string, many?: string): string {
  return `${fmt(n)} ${n === 1 ? word : (many ?? `${word}s`)}`;
}

/** "Harbor’s", "Brightpath Consulting’s", "Fieldworks’". */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`;
}

/** "cobalterp.com/financials/accounts-payable". */
export function hostPath(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
}

/** "$1.84" from cents. */
export function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Matches a typed name for the list search. */
export function matches(query: string, ...texts: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return texts.some((t) => (t ?? '').toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// Quote tokens: narrowing a quote word by word (rule 7)
// ---------------------------------------------------------------------------

export interface QuoteToken {
  text: string;
  /** A removed client name: kept whole, never cut. */
  removed: boolean;
  /** Code-point offsets inside the quote's own slice of the source. */
  start: number;
  end: number;
}

const codePoints = (s: string) => Array.from(s).length;

/**
 * The words of a stored quote with their offsets in the source slice. The
 * stored quote is the slice with each redaction range replaced by the
 * marker; `redactions` are code-point ranges relative to the slice. A
 * marker with no range is the source's own text (Caliber v1 records were
 * stored already redacted) and stands for itself. A narrowed quote runs
 * from the start of one token to the end of another, so it is always a
 * slice of the source and never splits a removed name.
 */
export function quoteTokens(
  excerpt: string,
  redactions: ReadonlyArray<{ start: number; end: number }>,
): QuoteToken[] {
  const chars = Array.from(excerpt);
  const mark = Array.from(CLIENT_NAME_REMOVED);
  const M = mark.length;
  // Where each redaction's marker sits in the stored quote.
  const markerAt = new Map<number, { start: number; end: number }>();
  let shift = 0;
  for (const r of [...redactions].sort((a, b) => a.start - b.start)) {
    markerAt.set(r.start + shift, r);
    shift += M - (r.end - r.start);
  }
  const isMark = (d: number) => mark.every((c, k) => chars[d + k] === c);
  const tokens: QuoteToken[] = [];
  let d = 0; // position in the stored quote
  let sp = 0; // position in the source slice
  while (d < chars.length) {
    if (isMark(d)) {
      const r = markerAt.get(d);
      tokens.push({
        text: CLIENT_NAME_REMOVED,
        removed: true,
        start: r ? r.start : sp,
        end: r ? r.end : sp + M,
      });
      sp = r ? r.end : sp + M;
      d += M;
      continue;
    }
    if (/\s/u.test(chars[d]!)) {
      d += 1;
      sp += 1;
      continue;
    }
    const from = sp;
    let text = '';
    while (d < chars.length && !/\s/u.test(chars[d]!) && !isMark(d)) {
      text += chars[d];
      d += 1;
      sp += 1;
    }
    tokens.push({ text, removed: false, start: from, end: sp });
  }
  return tokens;
}

/** The segment range of a narrowed quote: from token `from` to token `to`, inclusive. */
export function narrowRange(
  tokens: QuoteToken[],
  excerptStart: number,
  from: number,
  to: number,
): { start: number; end: number } | null {
  const a = tokens[Math.min(from, to)];
  const b = tokens[Math.max(from, to)];
  if (!a || !b) return null;
  return { start: excerptStart + a.start, end: excerptStart + b.end };
}

/** The review age in words: 365 → "12 months", 90 → "3 months", 45 → "45 days". */
export function reviewAgeText(days: number): string {
  if (days % 365 === 0) return plural((days / 365) * 12, 'month');
  if (days >= 60 && days % 30 === 0) return plural(days / 30, 'month');
  return plural(days, 'day');
}
